import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type {
  AppointmentType,
  Hold,
  Slot,
  SlotsResponse,
} from "@passa-a-pagina/shared";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSiteInfo } from "../components/StoreInfoCard";
import { useStoreHours, formatStoreHours } from "../lib/hours";
import "./appointments.css";

interface CreatedAppointment {
  id: string;
  type: AppointmentType;
  startTime: string;
  endTime: string;
  status: "confirmed";
  bookTitle: string | null;
}

function dayKey(d: Date): string {
  // YYYY-MM-DD in Europe/Lisbon — used to group slots by day (not displayed).
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Lisbon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(d);
}

function dayLabel(d: Date, locale: string): string {
  const fmt = new Intl.DateTimeFormat(locale, {
    timeZone: "Europe/Lisbon",
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
  return fmt.format(d);
}

function timeLabel(d: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: "Europe/Lisbon",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

interface SlotGroup {
  key: string;
  date: Date;
  label: string;
  slots: Slot[];
}

function groupByDay(slots: Slot[], locale: string): SlotGroup[] {
  const map = new Map<string, SlotGroup>();
  for (const s of slots) {
    const start = new Date(s.startTime);
    const key = dayKey(start);
    let group = map.get(key);
    if (!group) {
      group = { key, date: start, label: dayLabel(start, locale), slots: [] };
      map.set(key, group);
    }
    group.slots.push(s);
  }
  return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
}

export function Appointments() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt";
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const qc = useQueryClient();
  const { data: siteInfo } = useSiteInfo();
  const { data: storeHours } = useStoreHours();

  const typeParam = searchParams.get("type");
  const type: AppointmentType = typeParam === "dropoff" ? "dropoff" : "pickup";
  const holdIdParam = searchParams.get("holdId");

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [notes, setNotes] = useState("");
  const [confirmation, setConfirmation] = useState<CreatedAppointment | null>(null);
  const [walkInDone, setWalkInDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Authed: full booking + admin's own free/busy. Anon: read-only public view.
  const slotsQuery = useQuery<SlotsResponse, ApiError>({
    queryKey: ["appointments", "slots", user ? "auth" : "anon", type],
    queryFn: () =>
      user
        ? api<SlotsResponse>(`/api/appointments/slots?type=${type}`)
        : api<SlotsResponse>("/api/availability/public"),
    enabled: !authLoading,
    retry: false,
  });

  // For pickup with a holdId param, fetch the matching hold so we can show
  // "Marcar levantamento de <book>". Best-effort — if the hold isn't found
  // we just fall back to a generic copy.
  const holdsQuery = useQuery({
    queryKey: ["me", "holds"],
    queryFn: () => api<{ active: Hold[] }>("/api/me/holds"),
    enabled: Boolean(user && type === "pickup" && holdIdParam),
    retry: false,
  });
  const hold = useMemo(() => {
    if (!holdsQuery.data || !holdIdParam) return null;
    return holdsQuery.data.active.find((h) => h.id === holdIdParam) ?? null;
  }, [holdsQuery.data, holdIdParam]);

  const book = useMutation({
    mutationFn: (input: { type: AppointmentType; startTime: string; holdId?: string; notes?: string }) =>
      api<CreatedAppointment>("/api/appointments", { method: "POST", body: input }),
    onSuccess: (res) => {
      setConfirmation(res);
      qc.invalidateQueries({ queryKey: ["me", "appointments"] });
    },
    onError: (err: ApiError) => setSubmitError(err.message),
  });

  const walkIn = useMutation({
    mutationFn: (holdId: string) =>
      api<{ notified: true }>("/api/walk-in", { method: "POST", body: { holdId } }),
    onSuccess: () => setWalkInDone(true),
    onError: (err: ApiError) => setSubmitError(err.message),
  });

  if (authLoading) return <div className="container"><p>{t("common.loading")}</p></div>;

  // --- confirmation screens ---

  if (confirmation) {
    const start = new Date(confirmation.startTime);
    return (
      <div className="container appointments">
        <div className="surface appointments__confirm">
          <h1>{t("appointments.confirmTitle")}</h1>
          <p>
            <strong>{dayLabel(start, locale)}</strong>, {t("appointments.confirmAt")}{" "}
            <strong>{timeLabel(start, locale)}</strong>
            {confirmation.bookTitle && (
              <>
                {" — "}
                <em>{confirmation.bookTitle}</em>
              </>
            )}
            .
          </p>
          {siteInfo && (
            <p className="appointments__addr">
              {t("appointments.localLabel")}: <strong>{siteInfo.address}</strong>
            </p>
          )}
          <p className="appointments__hint">{t("appointments.confirmEmailHint")}</p>
          <div className="appointments__actions">
            <Link to="/dashboard" className="btn btn-primary">{t("appointments.confirmView")}</Link>
          </div>
        </div>
      </div>
    );
  }

  if (walkInDone) {
    return (
      <div className="container appointments">
        <div className="surface appointments__confirm">
          <h1>{t("appointments.walkInTitle")}</h1>
          <p>{t("appointments.walkInBody")}</p>
          {siteInfo && storeHours && (
            <p className="appointments__addr">
              <strong>{t("appointments.hoursLabel")}:</strong> {formatStoreHours(storeHours.days, t)} ·{" "}
              <strong>{t("appointments.localLabel")}:</strong> {siteInfo.address}
            </p>
          )}
          <div className="appointments__actions">
            <Link to="/dashboard" className="btn btn-primary">{t("appointments.walkInBack")}</Link>
          </div>
        </div>
      </div>
    );
  }

  // --- main view ---

  const slots = slotsQuery.data?.slots ?? [];
  const groups = groupByDay(slots.map((s) => ({ ...s })), locale);
  const activeGroup = groups.find((g) => g.key === selectedDay) ?? groups[0] ?? null;

  const heading =
    type === "pickup"
      ? hold
        ? t("appointments.headingPickupBook", { title: hold.bookTitle })
        : t("appointments.headingPickup")
      : t("appointments.headingDropoff");

  const intro = !user
    ? t("appointments.introAnon")
    : type === "pickup"
      ? t("appointments.introPickup")
      : t("appointments.introDropoff");

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!selectedSlot) {
      setSubmitError(t("appointments.selectFirst"));
      return;
    }
    book.mutate({
      type,
      startTime: selectedSlot.startTime,
      holdId: hold?.id,
      notes: notes.trim() || undefined,
    });
  }

  function onWalkIn() {
    if (!hold) {
      // Dropoff walk-in: no API call, just informational.
      setWalkInDone(true);
      return;
    }
    setSubmitError(null);
    walkIn.mutate(hold.id);
  }

  return (
    <div className="container appointments">
      <h1>{heading}</h1>
      <p className="appointments__intro">{intro}</p>

      {siteInfo && storeHours && (
        <p className="appointments__hint">
          <strong>{t("appointments.storeHoursLabel")}:</strong> {formatStoreHours(storeHours.days, t)} ·{" "}
          <strong>{t("appointments.localLabel")}:</strong> {siteInfo.address}
        </p>
      )}

      {slotsQuery.isLoading && <p>{t("appointments.loadingSlots")}</p>}
      {slotsQuery.error && (
        <p className="field__error" role="alert">{slotsQuery.error.message}</p>
      )}

      {slotsQuery.data && (
        <>
          {groups.length === 0 ? (
            <div className="surface appointments__empty">
              <p>{t("appointments.empty")}</p>
            </div>
          ) : (
            <>
              <div className="appointments__days" role="tablist" aria-label={t("appointments.daysAria")}>
                {groups.map((g) => {
                  const isActive = g.key === (selectedDay ?? activeGroup?.key);
                  const d = g.date;
                  return (
                    <button
                      key={g.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`appointments__day ${isActive ? "appointments__day--active" : ""}`}
                      onClick={() => {
                        setSelectedDay(g.key);
                        setSelectedSlot(null);
                      }}
                    >
                      <span className="appointments__day-week">
                        {new Intl.DateTimeFormat(locale, { timeZone: "Europe/Lisbon", weekday: "short" }).format(d)}
                      </span>
                      <span className="appointments__day-num">
                        {new Intl.DateTimeFormat(locale, { timeZone: "Europe/Lisbon", day: "2-digit" }).format(d)}
                      </span>
                      <span className="appointments__day-mon">
                        {new Intl.DateTimeFormat(locale, { timeZone: "Europe/Lisbon", month: "short" }).format(d)}
                      </span>
                    </button>
                  );
                })}
              </div>

              {activeGroup && (
                <div className="appointments__slots" role="group" aria-label={t("appointments.hoursAria")}>
                  <p className="appointments__slots-head">
                    {activeGroup.label} —{" "}
                    {t("appointments.slotsAvailable", { count: activeGroup.slots.length })}
                  </p>
                  <div className="appointments__slot-grid">
                    {activeGroup.slots.map((slot) => {
                      const start = new Date(slot.startTime);
                      const isSelected = selectedSlot?.startTime === slot.startTime;
                      return (
                        <button
                          key={slot.startTime}
                          type="button"
                          className={`appointments__slot ${isSelected ? "appointments__slot--selected" : ""}`}
                          onClick={() => setSelectedSlot(slot)}
                          disabled={!user}
                          aria-pressed={isSelected}
                        >
                          {timeLabel(start, locale)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {!user && (
            <div className="surface appointments__login-cta">
              <p>{t("appointments.loginCta")}</p>
              <Link to="/login" className="btn btn-primary">{t("appointments.loginBtn")}</Link>
            </div>
          )}

          {user && groups.length > 0 && (
            <form className="surface appointments__form" onSubmit={onSubmit}>
              <div className="field">
                <label htmlFor="appt-notes">{t("appointments.notesLabel")}</label>
                <textarea
                  id="appt-notes"
                  rows={2}
                  maxLength={500}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    type === "pickup"
                      ? t("appointments.notesPlaceholderPickup")
                      : t("appointments.notesPlaceholderDropoff")
                  }
                />
              </div>

              {submitError && (
                <p className="field__error" role="alert">{submitError}</p>
              )}

              <div className="appointments__actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!selectedSlot || book.isPending}
                >
                  {book.isPending ? t("appointments.booking") : t("appointments.book")}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onWalkIn}
                  disabled={walkIn.isPending}
                >
                  {walkIn.isPending ? t("appointments.walkInPending") : t("appointments.walkIn")}
                </button>
              </div>
              <p className="appointments__form-hint">
                {type === "pickup"
                  ? t("appointments.formHintPickup")
                  : t("appointments.formHintDropoff")}
              </p>
            </form>
          )}
        </>
      )}
    </div>
  );
}
